import {UIGameManager} from '../UIGameManager'
import {UIGameMapHighlightParameters, UIGameMapHighlightView} from '../ui_game_map/UIGameMapHighlightView'
import {UIInvisibleBackgroundView} from './UIInvisibleBackgroundView'
import {UIPlacementConfirmationButton} from './UIPlacementConfirmationButton'
import {UIGameActionConfirmationParameters} from '../ui_game_hover_actions/UIHoverActionController'
import {UIGameInitialPlacementsConfirmationController} from './UIGameInitialPlacementsConfirmationController'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {handleClickWithoutDragging} from '../../ui_utils/GestureUtils'
import {TurnState} from '@colonist/game-states'

export class UIGameConfirmActionController {
    private readonly uiGameManager: UIGameManager

    private placementButtons: UIPlacementConfirmationButton[] = []
    private whiteHighlightViews: UIGameMapHighlightView[] = []
    private invisibleBackground: UIInvisibleBackgroundView | undefined
    private selectedInitialPlacementIndex: number | undefined
    private _selectedLocationIndex?: number | undefined
    private readonly initialPlacementsConfirmationController: UIGameInitialPlacementsConfirmationController

    constructor(uiGameManager: UIGameManager) {
        this.uiGameManager = uiGameManager
        this.initialPlacementsConfirmationController = new UIGameInitialPlacementsConfirmationController(this.uiGameManager)
    }

    get selectedLocationIndex(): number | undefined {
        return this._selectedLocationIndex
    }

    setupInvisibleBackground(action: () => any) {
        this.removeInvisibleBackground()
        const mapView = this.uiGameManager.mapController.mapView
        const backgroundClickAction = () => {
            this.uiGameManager.mapController.hoverController.onHoverStateChange()
            action()
        }
        this.invisibleBackground = new UIInvisibleBackgroundView(-mapView.worldWidth, -mapView.worldHeight,
            mapView.worldWidth * 2, mapView.worldHeight * 2, mapView.layers.mapTiles, mapView, backgroundClickAction)
        this.invisibleBackground.drawBg()
        this.handleInvisibleBackgroundCancel()
    }

    private handleInvisibleBackgroundCancel() {
        const isUserOnNewMobileApp = UIUtilsEjsVariables.isUserOnMobileApp && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI
        if(!this.invisibleBackground) return
        if(!isUserOnNewMobileApp) {
            this.invisibleBackground?.on('click', () => this.removeVisualComponents())
            return
        }
        handleClickWithoutDragging(this.invisibleBackground, () => this.removeVisualComponents())
    }

    showActionConfirmation(highlightParameters: UIGameMapHighlightParameters, buttonX: number, buttonY: number, buttonSize: number,
                           cancelAction: () => void, allowedMechanics: UIGameActionConfirmationParameters[], placementIndex: number) {
        this.removeVisualComponents()
        this.highlightWhite(highlightParameters, cancelAction)
        this.initialPlacementsConfirmationController.highlightNeighborTilesAndPorts(placementIndex)
        this.sendSelectedInitialPlacementIndex(placementIndex)

        const maximumWidthOfButtonAndResources = buttonSize * 1.1
        let offsetX = - maximumWidthOfButtonAndResources * (allowedMechanics.length - 1) / 2
        for(const mechanic of allowedMechanics) {
            const texture = mechanic.texture
            const canTakeAction = mechanic.canTakeAction
            const bankActionAmount = mechanic.bankActionAmount
            const myColor = this.uiGameManager.gameController.myColor

            const mechanicButtonX = buttonX + offsetX
            const buttonAction = () => {
                this.cleanupConfirmation()
                mechanic.action(placementIndex)
            }
            const placementButton = new UIPlacementConfirmationButton(mechanicButtonX, buttonY, buttonSize, buttonSize,
                this.uiGameManager.mapController.mapView, buttonAction, texture, canTakeAction, myColor, bankActionAmount)
            this.placementButtons.push(placementButton)
            this._selectedLocationIndex = highlightParameters.index

            offsetX += maximumWidthOfButtonAndResources

            const resourcesToBuild = mechanic.resourcesToBuild
            placementButton.addResourceCardStates(resourcesToBuild, this.uiGameManager.cardController.getCardInventory())

            placementButton.zIndex = this.uiGameManager.mapController.mapView.layers.informationPopups
        }
    }

    private sendSelectedInitialPlacementIndex(placementIndex: number | undefined) {
        if(this.uiGameManager.gameController.currentState.turnState != TurnState.InitialPlacement) return
        if(!this.uiGameManager.gameController.isMyTurn) return
        if(this.selectedInitialPlacementIndex == placementIndex) return
        this.selectedInitialPlacementIndex = placementIndex
        this.uiGameManager.socketGameSend.selectedInitialPlacementIndex(placementIndex)
    }

    cleanupConfirmation() {
        this.removeInvisibleBackground()
        this.removeVisualComponents()
    }

    private removeVisualComponents() {
        this.removeWhiteHighlight()
        this.removePlacementConfirmButton()
        this.initialPlacementsConfirmationController.removeNeighborTilesAndPortsHighlights()
    }

    private highlightWhite(highlightParameters: UIGameMapHighlightParameters, action: () => any) {
        const whiteHighlightContainer = this.uiGameManager.mapController.mapView
        const whiteHighlightAction = () => {
            this.removeVisualComponents()
            action()
        }
        const whiteHighlightView = new UIGameMapHighlightView(this.uiGameManager, highlightParameters, whiteHighlightContainer, whiteHighlightAction, true)
        this.whiteHighlightViews.push(whiteHighlightView)
    }

    private removePlacementConfirmButton() {
        this.sendSelectedInitialPlacementIndex(undefined)
        for(const placementButton of this.placementButtons) {
            this.uiGameManager.mapController.mapView.removeChild(placementButton)
        }
        this.placementButtons = []
        this._selectedLocationIndex = undefined
    }

    private removeWhiteHighlight() {
        this.whiteHighlightViews.forEach((whiteHighlightView: UIGameMapHighlightView) => {
            this.uiGameManager.mapController.mapView.removeChild(whiteHighlightView)
        })
        this.whiteHighlightViews = []
    }

    private removeInvisibleBackground() {
        if(this.invisibleBackground == undefined) return
        this.invisibleBackground.parent.removeChild(this.invisibleBackground)
        this.invisibleBackground = undefined
    }
}
